import {useTranslation} from "react-i18next";

import SKU_PLAN_ASSET from "../../../../utilities/assets/SKU_PLAN";
import {numberFormat} from "../../../../utilities/moneyFormat";
import useSkuCatalogsService from "../../../SKU/SkuCatalogsService";
import {processImages} from "../../../SKU/VED/constants/functions";
import {
  formSkuTableAmount,
  formSkuTablePriceString,
  formSkuTableSpecPriceString,
  formSkuTableTotalSumString,
} from "../utils/functions";

import {procformatter} from "./utils/functionalFormatters";

const useKpFinDataService = () => {
  // const {i18n} = useTranslation();
  const {
    t,
    i18n: {language},
  } = useTranslation();

  const {lengthMeasurementQuantites, areaMeasurementQuantites, volumeMeasurementQuantites, weightMeasurementQuantites} =
    useSkuCatalogsService();

  const countOvhNew = (value, unit) => {
    if (value && unit && unit.length > 0) {
      const multiplier = parseFloat(unit[0].multiplier || 1);
      const result = value * multiplier;

      return result > 0
        ? numberFormat(result) + " " + (unit[0]?.title ? unit[0].title : language === "en" ? unit?.engName : unit?.name)
        : "";
    }
    return "";
  };

  const getOvh = (type, plan) => {
    if (!plan[type]) {
      return "";
    }

    const dimensionUnit = lengthMeasurementQuantites.filter(item => item.value === plan.overallSizeUnitId);
    const areaUnit = areaMeasurementQuantites.filter(item => item.value === plan.areaUnitId);
    const volumeUnit = volumeMeasurementQuantites.filter(item => item.value === plan.volumeUnitId);
    const weightUnit = weightMeasurementQuantites.filter(item => item.value === plan.weightUnitId);

    const dimenSh = [plan.length, plan.width, plan.height];

    if (!dimensionUnit && !areaUnit && !volumeUnit && !weightUnit && plan.palletCheck === false) {
      return "";
    }
    switch (type) {
      case "overallSizeCheck":
        if (dimenSh.some(item => item !== null)) {
          const multiplier = parseFloat(dimensionUnit[0]?.multiplier || 1);

          const updatedNumbers = dimenSh.map(num => {
            const cleaned = num === null || num === "0.00" ? 0 : parseFloat(num);
            return (cleaned * multiplier).toFixed(2);
          });

          const dimensionUnitString = dimensionUnit[0]?.title
            ? `${dimensionUnit[0]?.title}`
            : `${language === "en" ? plan.overallSizeUnit?.engName : plan.overallSizeUnit?.name}`;

          return `${updatedNumbers[0]}x${updatedNumbers[1]}x${updatedNumbers[2]} ${dimensionUnitString}`;
        }
        break;

      case "areaCheck":
        return countOvhNew(plan.area, areaUnit);

      case "volumeCheck":
        return countOvhNew(plan.volume, volumeUnit);

      case "weightCheck":
        return countOvhNew(plan.weight, weightUnit);

      case "palletCheck":
        if (plan.pallet) {
          return (
            [numberFormat(plan.pallet.length), numberFormat(plan.pallet.width)]
              .filter(e => e != null && e !== "")
              .join("×") + `(${plan.pallet.name})`
          );
        }

        break;

      default:
        break;
    }
  };

  const ovhJoinNew = plan => {
    const ovhKeys = ["overallSizeCheck", "areaCheck", "volumeCheck", "weightCheck", "palletCheck"];

    return ovhKeys
      .map(key => getOvh(key, plan))
      .filter(item => item)
      .join(", ");
  };

  const getKpFinTableData = plan => {
    const SKU_PLAN = new SKU_PLAN_ASSET(plan, language);
    const {skuPlanPrice, isMeasurementView, isRejected} = plan;

    const {topDiscountCheck, topDiscount, discount, discountCheck, additionalDiscount, averageSalesPrice} =
      skuPlanPrice ?? {};

    const paymentSumm = numberFormat(SKU_PLAN.paymentSum);

    const arePricesAndAmountExtended = isMeasurementView;
    const isAmountFractional = SKU_PLAN.isAmountFractional;

    return {
      number: plan?.sequenceNumber || null,
      article: SKU_PLAN.article,
      id: plan.skuPlanId,
      brandName: plan?.factory?.name,
      brandBlackList: Boolean(plan.factory?.blackList),
      skuName: plan.nameCheck ? plan.name : plan.nameEng,
      productGroupName: SKU_PLAN.productGroupName || "",
      characteristics: plan.charCheck ? SKU_PLAN.characteristicsText : "",
      //  plan.charCheck ? joinCharacteristics(plan) : "",
      ovh: SKU_PLAN.vwc,
      image1: SKU_PLAN.image1 || "",
      image2: SKU_PLAN.image2 || "",
      complectDesc: plan.complectDescCheck ? plan?.complectDesc : "",
      measurUnits: language === "en" ? plan?.sku?.measurementUnit?.engName : plan?.sku?.measurementUnit?.name,
      price: formSkuTablePriceString({averageSalesPrice, skuPlan: plan, language, t, arePricesAndAmountExtended}),
      discount:
        discountCheck && Number.parseFloat(discount)
          ? procformatter.format(discount / 100)
          : Number.parseFloat(additionalDiscount)
          ? procformatter.format(additionalDiscount / 100)
          : "",
      summ: formSkuTableSpecPriceString({skuPlan: plan, language, t, arePricesAndAmountExtended}),
      topDiscount: topDiscountCheck ? procformatter.format(topDiscount / 100) : "",
      totalSumm: formSkuTableTotalSumString({skuPlan: plan, language, t, arePricesAndAmountExtended}),
      amount: formSkuTableAmount({skuPlan: plan, t, language, arePricesAndAmountExtended}),
      // paymentSumm: toPay,
      paymentSumm,

      overallView: processImages(SKU_PLAN.overallImages),
      decorText: plan?.materialTypeCheck ? plan?.materialType : "",
      decorImages: processImages(SKU_PLAN.decorImages),
      colorText: plan?.colorTypeCheck ? plan?.colorType : "",
      colorImages: processImages(SKU_PLAN.colorImages),
      headerData: plan?.note,
      isPriceOutRange: !(
        parseFloat(averageSalesPrice) >= parseFloat(plan?.sku?.skuPrices?.[0]?.minPrice) &&
        parseFloat(averageSalesPrice) <= parseFloat(plan?.sku?.skuPrices?.[0]?.maxPrice)
      ),
      isAmountFractional,
      isRejected,
    };
  };

  const getKpFinTableDataForTemplates = plan => {
    const SKU_PLAN = new SKU_PLAN_ASSET(plan, language);

    const {
      // amount,

      skuPlanPrice: {
        topDiscountCheck,
        topDiscount,
        discount,
        discountCheck,
        additionalDiscount,
        averageSalesPrice,
        // finishPrice,
      },
    } = plan;

    // const afterDiscountSumForOne = discountCheck
    //   ? Number.parseFloat(averageSalesPrice) - (Number.parseFloat(averageSalesPrice) * discount) / 100
    //   : Number.parseFloat(additionalDiscount)
    //   ? Number.parseFloat(averageSalesPrice) - (Number.parseFloat(averageSalesPrice) * additionalDiscount) / 100
    //   : "";

    // const hasDiscount = Boolean(discountCheck || Number.parseFloat(additionalDiscount));

    // const sumToCalcTopDiscount = hasDiscount ? afterDiscountSumForOne : averageSalesPrice;

    // const afterTopDiscountSumForOne = topDiscountCheck
    //   ? sumToCalcTopDiscount - (sumToCalcTopDiscount * topDiscount) / 100
    //   : "";

    // const toPay = topDiscountCheck
    //   ? afterTopDiscountSumForOne * Number(amount)
    //   : (hasDiscount ? afterDiscountSumForOne : averageSalesPrice) * Number(amount);

    // const {color, material, batch, productSeries} = plan;

    const paymentSumm = numberFormat(SKU_PLAN.paymentSum);
    const isAmountFractional = SKU_PLAN.isAmountFractional;

    return {
      number: plan?.sequenceNumber || null,
      article: SKU_PLAN.article,
      id: plan.skuTemplateId,
      brandName: plan.factory?.name,
      brandBlackList: Boolean(plan?.factory?.blackList),
      skuName: (plan.nameCheck ? plan.name : plan.nameEng) || plan?.name || plan?.nameEng || "",
      productGroupName: SKU_PLAN.productGroupName || "",
      characteristics: plan.charCheck ? SKU_PLAN.characteristicsText : SKU_PLAN.characteristicsText || "",
      // plan.charCheck
      //   ? plan.characteristicText
      //     ? plan.characteristicText
      //     : formSKUCharacteristics({color, material, batch, productSeries, t})
      //   : "",
      //  plan.charCheck ? joinCharacteristics(plan) : "",
      ovh: SKU_PLAN.vwc,
      // ovhJoinNew(plan),
      image1: SKU_PLAN.image1 || "",
      image2: SKU_PLAN.image2 || "",
      complectDesc: plan.complectDescCheck ? plan.complectDesc : plan.complectDesc || "",
      measurUnits: language === "en" ? plan?.sku?.measurementUnit?.engName : plan?.sku?.measurementUnit?.name,
      price: formSkuTablePriceString({averageSalesPrice, skuPlan: plan, language, t}),
      discount:
        discountCheck && Number.parseFloat(discount)
          ? procformatter.format(discount / 100)
          : Number.parseFloat(additionalDiscount)
          ? procformatter.format(additionalDiscount / 100)
          : "",
      summ: formSkuTableSpecPriceString({skuPlan: plan, language, t}),
      topDiscount: topDiscountCheck ? procformatter.format(topDiscount / 100) : "",
      totalSumm: formSkuTableTotalSumString({skuPlan: plan, language, t}),
      amount: formSkuTableAmount({skuPlan: plan, t, language}),
      // paymentSumm: toPay,
      paymentSumm,

      overallView: processImages(SKU_PLAN.overallImages),
      decorText: plan?.materialTypeCheck ? plan?.materialType : "",
      decorImages: processImages(SKU_PLAN.decorImages),
      colorText: plan?.colorTypeCheck ? plan?.colorType : "",
      colorImages: processImages(SKU_PLAN.colorImages),
      headerData: plan?.note,
      isPriceOutRange: !(
        parseFloat(averageSalesPrice) >= parseFloat(plan.sku.skuPrices[0].minPrice) &&
        parseFloat(averageSalesPrice) <= parseFloat(plan.sku.skuPrices[0].maxPrice)
      ),
      isAmountFractional,
    };
  };

  const getRequestSkuTableData = (sku, chosenLanguage) => {
    const skuLanguage = chosenLanguage || language;
    const {
      // amount, quantity, totalInObject,
      skuPlanPrice,
    } = sku;

    const SKU_PLAN = new SKU_PLAN_ASSET(sku, skuLanguage);

    const {averageSalesPrice, discountCheck, discount, topDiscountCheck, topDiscount, additionalDiscount} =
      skuPlanPrice ?? {};
    const isAmountFractional = SKU_PLAN.isAmountFractional;

    return {
      number: sku.sequenceNumber,
      article: SKU_PLAN.article,

      id: sku.skuRequestId,
      brandName: sku?.factory?.name,
      brandBlackList: Boolean(sku.factory?.blackList),
      skuName: skuLanguage === "en" ? sku.nameEng : sku.name,

      characteristics: sku.charCheck
        ? skuLanguage === "en"
          ? SKU_PLAN.characteristicsTextEng
          : SKU_PLAN.characteristicsText
        : "",
      ovh: ovhJoinNew(sku),
      image1: SKU_PLAN.image1 || "",
      image2: SKU_PLAN.image2 || "",
      complectDesc: sku.complectDescCheck
        ? skuLanguage === "en"
          ? sku.complectDescEng || sku.complectDesc
          : sku.complectDesc
        : "",

      // price: numberFormat(+averageSalesPrice * +quantity * +totalInObject),
      // price: totalSumm || summ || formSkuTablePriceString({averageSalesPrice, skuPlan: sku, language, t}),
      price: formSkuTablePriceString({averageSalesPrice, skuPlan: sku, language, t}),
      discount:
        discountCheck && Number.parseFloat(discount)
          ? procformatter.format(discount / 100)
          : Number.parseFloat(additionalDiscount)
          ? procformatter.format(additionalDiscount / 100)
          : "",
      summ: formSkuTableSpecPriceString({skuPlan: sku, language, t}),
      topDiscount: topDiscountCheck ? procformatter.format(topDiscount / 100) : "",
      totalSumm: formSkuTableTotalSumString({skuPlan: sku, language, t}),
      amount: formSkuTableAmount({skuPlan: sku, t, language}),
      paymentSumm: numberFormat(SKU_PLAN.paymentSum),

      // paymentSumm: numberFormat(Math.round(+averageSalesPrice * +quantity * +totalInObject * +amount * 1000) / 1000),
      decorText: sku?.materialTypeCheck
        ? skuLanguage === "en"
          ? sku.materialTypeEng || sku.materialType
          : sku.materialType
        : "",
      decorImages: processImages(SKU_PLAN.decorImages),
      colorText: sku?.colorTypeCheck ? (skuLanguage === "en" ? sku.colorTypeEng || sku.colorType : sku.colorType) : "",
      colorImages: processImages(SKU_PLAN.colorImages),
      overallView: null,

      headerData: sku?.note,
      isAmountFractional,
    };
  };

  const getRequestVarificationSkuTableData = sku => {
    const {
      // amount,

      skuPlanPrice,
    } = sku;

    const {topDiscountCheck, topDiscount, discount, discountCheck, additionalDiscount, averageSalesPrice} =
      skuPlanPrice ?? {};
    const SKU_PLAN = new SKU_PLAN_ASSET(sku, language);
    const isAmountFractional = SKU_PLAN.isAmountFractional;

    // const {amount, listPrice} = sku;
    // var summ = listPrice * (amount || 1);

    return {
      number: sku.sequenceNumber,
      article: SKU_PLAN.article,

      id: sku.skuRequestId,
      brandName: sku.factory?.name,
      brandBlackList: Boolean(sku.factory?.blackList),
      skuName: sku.nameCheck ? sku.name : sku.nameEng,
      // characteristics: sku.charCheck ? joinCharacteristics(sku) : "",
      characteristics: sku.charCheck ? SKU_PLAN.characteristicsText : "",
      ovh: SKU_PLAN.vwc,
      // ovhJoinNew(sku),
      image1: SKU_PLAN.image1 || "",
      image2: SKU_PLAN.image2 || "",
      complectDesc: sku.complectDescCheck ? sku.complectDesc : "",
      measurUnits: language === "en" ? sku?.sku?.measurementUnit?.engName : sku?.sku?.measurementUnit?.name,
      price: formSkuTablePriceString({averageSalesPrice, skuPlan: sku, language, t}),
      // amount: sku.amount,
      discount:
        discountCheck && Number.parseFloat(discount)
          ? procformatter.format(discount / 100)
          : Number.parseFloat(additionalDiscount)
          ? procformatter.format(additionalDiscount / 100)
          : "",
      summ: formSkuTableSpecPriceString({skuPlan: sku, language, t}),
      topDiscount: topDiscountCheck ? procformatter.format(topDiscount / 100) : "",
      totalSumm: formSkuTableTotalSumString({skuPlan: sku, language, t}),
      amount: formSkuTableAmount({skuPlan: sku, t, language}),
      // price: listPrice,
      // paymentSumm: summ,
      paymentSumm: numberFormat(SKU_PLAN.paymentSum),
      decorText: sku?.materialTypeCheck ? sku?.materialType : "",
      decorImages: processImages(SKU_PLAN.decorImages),
      colorText: sku?.colorTypeCheck ? sku?.colorType : "",
      colorImages: processImages(SKU_PLAN.colorImages),
      headerData: sku?.note,
      isSelectFroze: sku?.isInspected,
      isAmountFractional,
    };
  };

  return {getKpFinTableData, getRequestSkuTableData, getRequestVarificationSkuTableData, getKpFinTableDataForTemplates};
};

export default useKpFinDataService;
